import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, Activity, Shield, Cpu, Zap, Lock, RefreshCw,
  Wifi, Bug, AlertOctagon, Network, Database, Terminal, Radio,
  CloudOff, Radar, Wrench, Laptop2, Play, StopCircle, X,
  HardDrive, Server, Settings, Code, UserX, Package
} from 'lucide-react';
import { Notification } from './NotificationCenter';
import { Asset } from '../types';

interface AttackSimulationProps {
  onNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
}

interface AssetStatus {
  id: string;
  name: string;
  type: string;
  status: 'normal' | 'warning' | 'critical';
  metrics: {
    name: string;
    value: number;
    unit: string;
    trend: 'up' | 'down' | 'stable';
  }[];
}

export const AttackSimulation: React.FC<AttackSimulationProps> = ({ onNotification }) => {
  const [isSimulating, setIsSimulating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedAttack, setSelectedAttack] = useState<string | null>(null);
  const [affectedAssets, setAffectedAssets] = useState<AssetStatus[]>([
    {
      id: 'network',
      name: 'Network Infrastructure',
      type: 'infrastructure',
      status: 'normal',
      metrics: [
        { name: 'Bandwidth', value: 100, unit: 'Mbps', trend: 'stable' },
        { name: 'Latency', value: 5, unit: 'ms', trend: 'stable' },
        { name: 'Packet Loss', value: 0.1, unit: '%', trend: 'stable' }
      ]
    },
    {
      id: 'database',
      name: 'Database Server',
      type: 'server',
      status: 'normal',
      metrics: [
        { name: 'CPU Usage', value: 45, unit: '%', trend: 'stable' },
        { name: 'Memory', value: 60, unit: '%', trend: 'stable' },
        { name: 'Query Time', value: 50, unit: 'ms', trend: 'stable' }
      ]
    },
    {
      id: 'scada',
      name: 'SCADA System',
      type: 'control',
      status: 'normal',
      metrics: [
        { name: 'Response Time', value: 100, unit: 'ms', trend: 'stable' },
        { name: 'Commands/sec', value: 50, unit: 'ops', trend: 'stable' },
        { name: 'Error Rate', value: 0.01, unit: '%', trend: 'stable' }
      ]
    },
    {
      id: 'firmware',
      name: 'Device Firmware',
      type: 'embedded',
      status: 'normal',
      metrics: [
        { name: 'Version Check', value: 100, unit: '%', trend: 'stable' },
        { name: 'Integrity', value: 100, unit: '%', trend: 'stable' },
        { name: 'Update Status', value: 100, unit: '%', trend: 'stable' }
      ]
    },
    {
      id: 'protocol',
      name: 'Communication Protocol',
      type: 'network',
      status: 'normal',
      metrics: [
        { name: 'Validation Rate', value: 100, unit: '%', trend: 'stable' },
        { name: 'Encryption', value: 100, unit: '%', trend: 'stable' },
        { name: 'Protocol Errors', value: 0, unit: 'count', trend: 'stable' }
      ]
    }
  ]);

  const attacks = [
    {
      id: 'dos',
      name: 'Denial of Service',
      description: 'Simulates a DoS attack targeting system availability',
      icon: Network,
      duration: 20000,
      severity: 'high',
      affects: ['network', 'scada'],
      impact: {
        network: {
          bandwidth: { min: -80, max: -95 },
          latency: { min: 500, max: 1000 },
          packetLoss: { min: 15, max: 25 }
        },
        scada: {
          responseTime: { min: 300, max: 500 },
          commandRate: { min: -60, max: -80 }
        }
      }
    },
    {
      id: 'mitm',
      name: 'Man in the Middle',
      description: 'Simulates interception of system communications',
      icon: Wifi,
      duration: 25000,
      severity: 'critical',
      affects: ['network', 'protocol'],
      impact: {
        network: {
          latency: { min: 100, max: 200 },
          packetLoss: { min: 5, max: 10 }
        },
        protocol: {
          validationRate: { min: -40, max: -60 },
          encryption: { min: -30, max: -50 }
        }
      }
    },
    {
      id: 'malware',
      name: 'Malware Infection',
      description: 'Simulates malicious software infection',
      icon: Bug,
      duration: 30000,
      severity: 'critical',
      affects: ['scada', 'database'],
      impact: {
        scada: {
          responseTime: { min: 200, max: 400 },
          errorRate: { min: 10, max: 20 }
        },
        database: {
          cpu: { min: 70, max: 90 },
          memory: { min: 75, max: 90 }
        }
      }
    },
    {
      id: 'ransomware',
      name: 'Ransomware Attack',
      description: 'Simulates ransomware encryption of critical data',
      icon: Lock,
      duration: 15000,
      severity: 'critical',
      affects: ['database', 'scada'],
      impact: {
        database: {
          queryTime: { min: 400, max: 800 },
          cpu: { min: 80, max: 95 }
        },
        scada: {
          responseTime: { min: 500, max: 1000 },
          errorRate: { min: 20, max: 40 }
        }
      }
    },
    {
      id: 'firmware',
      name: 'Firmware Attack',
      description: 'Simulates compromised device firmware',
      icon: HardDrive,
      duration: 35000,
      severity: 'high',
      affects: ['firmware', 'scada'],
      impact: {
        firmware: {
          integrity: { min: -40, max: -60 },
          versionCheck: { min: -50, max: -70 }
        },
        scada: {
          errorRate: { min: 15, max: 25 },
          commandRate: { min: -30, max: -50 }
        }
      }
    },
    {
      id: 'protocol',
      name: 'Protocol Manipulation',
      description: 'Simulates protocol manipulation attacks',
      icon: Terminal,
      duration: 22000,
      severity: 'high',
      affects: ['protocol', 'network'],
      impact: {
        protocol: {
          validationRate: { min: -60, max: -80 },
          protocolErrors: { min: 50, max: 100 }
        },
        network: {
          packetLoss: { min: 10, max: 20 },
          latency: { min: 200, max: 400 }
        }
      }
    },
    {
      id: 'replay',
      name: 'Command Replay',
      description: 'Simulates replay attacks on control commands',
      icon: RefreshCw,
      duration: 18000,
      severity: 'high',
      affects: ['scada', 'protocol'],
      impact: {
        scada: {
          commandRate: { min: 200, max: 400 },
          errorRate: { min: 30, max: 50 }
        },
        protocol: {
          validationRate: { min: -30, max: -50 },
          protocolErrors: { min: 30, max: 60 }
        }
      }
    },
    {
      id: 'zeroday',
      name: 'Zero-Day Exploit',
      description: 'Simulates unknown vulnerability exploitation',
      icon: AlertOctagon,
      duration: 40000,
      severity: 'critical',
      affects: ['scada', 'database', 'network'],
      impact: {
        scada: {
          responseTime: { min: 300, max: 600 },
          errorRate: { min: 25, max: 45 }
        },
        database: {
          cpu: { min: 85, max: 95 },
          queryTime: { min: 300, max: 600 }
        },
        network: {
          packetLoss: { min: 15, max: 30 },
          latency: { min: 300, max: 600 }
        }
      }
    },
    {
      id: 'supplychain',
      name: 'Supply Chain Attack',
      description: 'Simulates compromised system components',
      icon: Package,
      duration: 45000,
      severity: 'critical',
      affects: ['firmware', 'scada', 'database'],
      impact: {
        firmware: {
          integrity: { min: -50, max: -70 },
          updateStatus: { min: -40, max: -60 }
        },
        scada: {
          errorRate: { min: 20, max: 40 },
          responseTime: { min: 200, max: 400 }
        },
        database: {
          queryTime: { min: 200, max: 400 },
          cpu: { min: 70, max: 90 }
        }
      }
    },
    {
      id: 'insider',
      name: 'Insider Threat',
      description: 'Simulates malicious insider activities',
      icon: UserX,
      duration: 30000,
      severity: 'high',
      affects: ['database', 'scada', 'network'],
      impact: {
        database: {
          queryTime: { min: 150, max: 300 },
          cpu: { min: 60, max: 80 }
        },
        scada: {
          commandRate: { min: -20, max: -40 },
          errorRate: { min: 15, max: 30 }
        },
        network: {
          bandwidth: { min: -30, max: -50 },
          latency: { min: 100, max: 200 }
        }
      }
    }
  ];

  let simulationInterval: NodeJS.Timeout;

  const startSimulation = async () => {
    if (!selectedAttack) return;

    const attack = attacks.find(a => a.id === selectedAttack);
    if (!attack) return;

    setIsSimulating(true);
    setProgress(0);

    // Initial attack notification
    onNotification({
      type: 'warning',
      title: 'Attack Simulation Started',
      message: `Starting ${attack.name} simulation...`
    });

    // Notify about affected assets
    const affectedAssetNames = attack.affects.map(assetId => 
      affectedAssets.find(asset => asset.id === assetId)?.name
    ).filter(Boolean);

    onNotification({
      type: 'warning',
      title: 'Assets Under Attack',
      message: `Affected systems: ${affectedAssetNames.join(', ')}`
    });

    // Show potential impact notification
    const impactDescription = attack.affects.map(assetId => {
      const asset = affectedAssets.find(a => a.id === assetId);
      const impact = attack.impact[assetId as keyof typeof attack.impact];
      if (!asset || !impact) return null;

      const impactDetails = Object.entries(impact).map(([metric, range]) => {
        return `${metric.replace(/([A-Z])/g, ' $1').toLowerCase()}: ${range.min}% to ${range.max}%`;
      }).join(', ');

      return `${asset.name}: ${impactDetails}`;
    }).filter(Boolean);

    onNotification({
      type: 'warning',
      title: 'Potential Impact',
      message: `Expected system degradation:\n${impactDescription.join('\n')}`
    });

    const startTime = Date.now();
    const duration = attack.duration;

    // Update affected assets
    setAffectedAssets(prev => prev.map(asset => ({
      ...asset,
      status: attack.affects.includes(asset.id) ? 'warning' : 'normal'
    })));

    simulationInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);

      // Update asset metrics based on attack impact
      setAffectedAssets(prev => prev.map(asset => {
        if (!attack.affects.includes(asset.id)) return asset;

        const impact = attack.impact[asset.id as keyof typeof attack.impact];
        if (!impact) return asset;

        return {
          ...asset,
          status: newProgress > 50 ? 'critical' : 'warning',
          metrics: asset.metrics.map(metric => {
            const impactRange = impact[metric.name.toLowerCase().replace(/\s/g, '') as keyof typeof impact];
            if (!impactRange) return metric;

            const severity = Math.min(newProgress / 100, 1);
            const impactValue = (impactRange.min + (impactRange.max - impactRange.min) * Math.random()) * severity;
            
            return {
              ...metric,
              value: Math.max(0, metric.value + impactValue),
              trend: impactValue < 0 ? 'down' : 'up'
            };
          })
        };
      }));

      // Add critical state notification when assets reach critical status
      if (newProgress > 50) {
        const criticalAssets = affectedAssets
          .filter(asset => attack.affects.includes(asset.id) && asset.status === 'critical')
          .map(asset => asset.name);

        if (criticalAssets.length > 0) {
          onNotification({
            type: 'attack',
            title: 'Critical Asset Status',
            message: `Systems in critical state: ${criticalAssets.join(', ')}`
          });
        }
      }

      if (elapsed >= duration) {
        clearInterval(simulationInterval);
        stopSimulation();
      }
    }, 1000);
  };

  const stopSimulation = () => {
    clearInterval(simulationInterval);
    setIsSimulating(false);
    setProgress(0);

    // Reset asset states
    setAffectedAssets(prev => prev.map(asset => ({
      ...asset,
      status: 'normal',
      metrics: asset.metrics.map(metric => ({
        ...metric,
        trend: 'stable'
      }))
    })));

    const attack = attacks.find(a => a.id === selectedAttack);
    if (attack) {
      onNotification({
        type: 'success',
        title: 'Attack Simulation Stopped',
        message: `${attack.name} simulation has been stopped and systems restored.`
      });
    }
  };

  useEffect(() => {
    return () => {
      if (simulationInterval) {
        clearInterval(simulationInterval);
      }
    };
  }, []);

  const getStatusColor = (status: AssetStatus['status']) => {
    switch (status) {
      case 'normal':
        return 'text-green-500 bg-green-50';
      case 'warning':
        return 'text-yellow-500 bg-yellow-50';
      case 'critical':
        return 'text-red-500 bg-red-50';
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <span className="text-red-500">↑</span>;
      case 'down':
        return <span className="text-green-500">↓</span>;
      default:
        return <span className="text-gray-500">→</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Shield className="h-6 w-6 text-red-500" />
            <h2 className="text-xl font-bold text-gray-900">Attack Simulation</h2>
          </div>
          <div className="flex items-center space-x-4">
            {isSimulating ? (
              <button
                onClick={stopSimulation}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <StopCircle className="h-4 w-4" />
                <span>Stop Simulation</span>
              </button>
            ) : (
              <button
                onClick={startSimulation}
                disabled={!selectedAttack}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                  !selectedAttack
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                <Play className="h-4 w-4" />
                <span>Start Simulation</span>
              </button>
            )}
          </div>
        </div>

        {/* Attack Types */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {attacks.map(attack => (
            <div
              key={attack.id}
              onClick={() => !isSimulating && setSelectedAttack(attack.id)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedAttack === attack.id
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 hover:border-red-300'
              } ${isSimulating ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center space-x-3 mb-3">
                <attack.icon className="h-6 w-6 text-red-500" />
                <h3 className="font-medium text-gray-900">{attack.name}</h3>
              </div>
              <p className="text-sm text-gray-500 mb-3">{attack.description}</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                attack.severity === 'critical'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {attack.severity}
              </span>
            </div>
          ))}
        </div>

        {/* Progress Bar */}
        {isSimulating && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Attack Progress</span>
              <span className="text-sm font-medium text-gray-700">{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-red-600 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Asset Status Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {affectedAssets.map(asset => (
            <div
              key={asset.id}
              className={`p-4 rounded-lg border transition-all ${
                asset.status === 'normal'
                  ? 'border-gray-200'
                  : asset.status === 'warning'
                  ? 'border-yellow-300 bg-yellow-50'
                  : 'border-red-300 bg-red-50'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-gray-900">{asset.name}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                  {asset.status.toUpperCase()}
                </span>
              </div>
              <div className="space-y-2">
                {asset.metrics.map((metric, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{metric.name}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">
                        {metric.value.toFixed(1)} {metric.unit}
                      </span>
                      {getTrendIcon(metric.trend)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Network Status</h3>
            <Network className={`h-5 w-5 ${isSimulating ? 'text-yellow-500' : 'text-green-500'}`} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Uptime</span>
            <span className="text-sm font-medium text-gray-900">
              {isSimulating ? '98.5%' : '99.9%'}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Firewall Status</h3>
            <Shield className={`h-5 w-5 ${isSimulating ? 'text-red-500' : 'text-green-500'}`} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Threats Blocked</span>
            <span className="text-sm font-medium text-gray-900">
              {isSimulating ? '1,458' : '234'}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">System Load</h3>
            <Cpu className={`h-5 w-5 ${isSimulating ? 'text-red-500' : 'text-yellow-500'}`} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">CPU Usage</span>
            <span className="text-sm font-medium text-gray-900">
              {isSimulating ? '92%' : '78%'}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Security Score</h3>
            <Lock className={`h-5 w-5 ${isSimulating ? 'text-yellow-500' : 'text-green-500'}`} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Overall Rating</span>
            <span className="text-sm font-medium text-gray-900">
              {isSimulating ? 'C' : 'A+'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};